import React, { useState, useCallback, useEffect } from 'react';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import './registrationpricesetup.css';

import { handleAddData, getData as getPriceSetup } from '../../services/registrationPriceSetupServices';
import { getData } from '../../services/registrationTypesListServices';
import { getData as getRegistrationDates } from '../../services/registrationDatesServices';

const RegistrationPriceSetup = () => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [registrationPriceType, setRegistrationPriceType] = useState([]);
    const [registrationDates, setRegistrationDates] = useState([]);
    const [regDateId, setRegDateId] = useState(0);
    const [inputValues, setInputValues] = useState([]);
    const [setupPriceData, setSetupPriceData] = useState([]);

    useEffect(() => {
        if (registrationPriceType.length > 0 && setupPriceData.length > 0) {            
            setInputValues(
                registrationPriceType.map((item) => {
                    const typePriceData = setupPriceData.find(d => Number(d.regtype_id) === Number(item.id));
                    return [
                        { usd: typePriceData ? typePriceData.early_USD || '' : '', euro: typePriceData ? typePriceData.early_EUR || '' : '' },
                        { usd: typePriceData ? typePriceData.mid_USD || '' : '', euro: typePriceData ? typePriceData.mid_EUR || '' : '' },
                        { usd: typePriceData ? typePriceData.final_USD || '' : '', euro: typePriceData ? typePriceData.final_EUR || '' : '' }
                    ];
                })
            );
        }
    }, [registrationPriceType, setupPriceData]);

    const handleInputChange = (typeId, dateId, field, value) => {
        if (value === '' || Number(value) < 10) {
            setErrors(prev => ({
                ...prev,
                [`${typeId}_${dateId}_${field}`]: 'Value must be at least 2 digits (10 or more)'
            }));
        } else {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`${typeId}_${dateId}_${field}`];
                return newErrors;
            });
        }
        setInputValues(prev => {
            const updated = prev.map((row, i) =>
                i === typeId ? row.map((cell, j) =>
                    j === dateId ? { ...cell, [field]: value } : cell
                ) : row
            );
            return updated;
        });
    };

    const isFormValid = Object.keys(errors).length === 0 && inputValues.every(row => row.every(cell => cell.usd && cell.euro && Number(cell.usd) > 0 && Number(cell.euro) > 0));

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [regPriceType, regDates, getPriceSetupData] = await Promise.all([getData(), getRegistrationDates(), getPriceSetup()]);
            setSetupPriceData(getPriceSetupData);
            setRegistrationPriceType(regPriceType.
                slice() // optional: makes a copy to avoid mutating original array
                .sort((a, b) => Number(a.reg_type_order) - Number(b.reg_type_order)) 
                .map(item => {
                return {
                    id: item.id,
                    label: item.type_name,
                    order: item.reg_type_order
                };
            })
            );
            const selectedDate = regDates[regDates.length - 1];
            setRegDateId(selectedDate['id']);
            setRegistrationDates([{ date: selectedDate['conedate'], label: selectedDate['conetitle'] },
            { date: selectedDate['ctwodate'], label: selectedDate['ctwotitle'] },
            { date: selectedDate['cthreedate'], label: selectedDate['cthreetitle'] }]);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        finally {
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSave = async () => {
        const result = [];
        const labelMap = {
            'Early Bird': 'early',
            'Mid Term': 'mid',
            'Final Call': 'final'
        }
        registrationPriceType?.forEach((type, typeId) => {
            const entry = {};
            registrationDates.forEach((date, dateId) => {
                entry[`${labelMap[date.label.trim()]}_USD`] = Number(inputValues[typeId][dateId].usd);
                entry[`${labelMap[date.label.trim()]}_EUR`] = Number(inputValues[typeId][dateId].euro);
            });
            entry['regdate_id'] = regDateId;
            entry['regtype_id'] = type.id;
            entry['reg_type_order'] = type.order;
            result.push(entry);
        });
        try {
            const response = await handleAddData(result);
             if (response && response.length > 0) {
                toast.success("Saved Successfully!", {
                              position: 'top-right',
                              autoClose: 3000,
                            });
             }
            console.log('Registration type added:', response);
        } catch (error) {
            toast.error(result.message ||"Failed to save data. Please try again.", {
                      position: 'top-right',
                      });
            console.error('Error adding registration type:', error);
        }
    };

    return (
        <div className="d-flex">
            <Sidebar />
            <div className="main-content w-100">
                <Header />
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
                        <Spinner />
                    </div>
                ) : (
                    <div>
                        <div className="container-fluid p-4">
                            <div className="row">
                                <div className="col-8">
                                    <p className='mb-3 fs-4'>Registration <b>/ Registration Price Setup</b></p>
                                </div>
                            </div>
                            <table className="rps-table">
                                <thead>
                                    <tr>
                                        <th style={{textAlign:'left'}}>Type / Date</th>
                                        {registrationDates?.map((date) => (
                                            <th key={date.label}>{date.label} <br />({date.date})</th>
                                        ))}
                                        {/* <th>Order</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                {registrationPriceType?.map((type, typeId) => (
                                        <tr key={type.id}>
                                            <td style={{textAlign:'left'}}> <strong>[{type?.order}] </strong>{type?.label}</td>
                                            {registrationDates.map((date, dateId) => (
                                                <td key={date.label + type.id}>
                                                    <div className="rps-input-group">
                                                        <span>$</span>
                                                        <input
                                                            type="number"
                                                            placeholder='usd'
                                                            className={`rps-input${errors[`${typeId}_${dateId}_usd`] ? ' is-invalid' : ''}`}
                                                            min="10"
                                                            step="any"
                                                            value={inputValues[typeId][dateId]?.usd}
                                                            onChange={e => handleInputChange(typeId, dateId, 'usd', e.target.value)}
                                                        />
                                                        <span>€</span>
                                                        <input
                                                            type="number"
                                                            placeholder='euro'
                                                            className={`rps-input${errors[`${typeId}_${dateId}_euro`] ? ' is-invalid' : ''}`}
                                                            min="10"
                                                            step="any"
                                                            value={inputValues[typeId][dateId].euro}
                                                            onChange={e => handleInputChange(typeId, dateId, 'euro', e.target.value)}
                                                        />
                                                    </div>
                                                    {errors[`${typeId}_${dateId}_usd`] && (
                                                        <div className="invalid-feedback d-block">{errors[`${typeId}_${dateId}_usd`]}</div>
                                                    )}
                                                    {errors[`${typeId}_${dateId}_euro`] && (
                                                        <div className="invalid-feedback d-block">{errors[`${typeId}_${dateId}_euro`]}</div>
                                                    )}
                                                </td>
                                            ))}
                                            {/* <td>
                                                <select
                                                    key={type.id + 'qty'}
                                                    value={orderValues[typeId]}
                                                    onChange={e => handleOrderChange(typeId, e.target.value)}
                                                >
                                                    {registrationPriceType.map((_, id) => (
                                                        <option key={id + 1} value={id + 1}>{id + 1}</option>
                                                    ))}
                                                </select>
                                            </td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="row">
                                <div className="col-8">
                                    <button className="btn btn-primary mt-3" onClick={handleSave} disabled={!isFormValid}>Submit</button>
                                </div>
                            </div>
                        </div>
                        <Footer />
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegistrationPriceSetup;
